import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { formatHourMask } from '../../utils/formatHourMask';
import { ptBR } from 'date-fns/locale';
import { htmlToTextareaValue } from '../../utils/html';
import 'react-datepicker/dist/react-datepicker.css';
import './Filter.css';

function Filter({ type = 'text', icon: Icon, placeholder, value, onChange, options = [], onSearch, disabled = false, resizable = false, height = 100, fluid = true }) {
  const [open, setOpen] = useState(false);
 const baseClass =
   type === 'multi-select'
     ? 'filtro-input dropdown multi-select'
     : (type === 'select' || type === 'autocomplete' || type === 'multi-autocomplete')
       ? 'filtro-input dropdown'
       : 'filtro-input';
  const containerClass = `${baseClass} ${disabled ? 'filtro-disabled' : ''} ${!fluid ? 'filtro-not-fluid' : ''}`;
  const [inputValue, setInputValue] = useState(value || '');
  const [dynamicOptions, setDynamicOptions] = useState([]);
  const [search, setSearch] = useState('');
  const ref = useRef();
  const inputRef = useRef(null);

  useEffect(() => {
    if (type === 'autocomplete') {
      if (!value) {
        setInputValue('');
      } else if (typeof value === 'object') {
        setInputValue(value.label);
      } else {
        setInputValue(value);
      }
    }
  }, [value, type]);

    useEffect(() => {
    if (type === 'autocomplete' && inputValue.length >= 3) {
      const delayDebounce = setTimeout(() => {
        onSearch?.(inputValue).then((results) => {
          setDynamicOptions(results);
          if (inputRef.current === document.activeElement) {
            setOpen(true);
          } else {
            setOpen(false);
          }
        });
      }, 300);

      return () => clearTimeout(delayDebounce);
    } else if (type === 'autocomplete') {
      setDynamicOptions([]);
      setOpen(false);
    }
  }, [inputValue, onSearch, type]);

  useEffect(() => {
    const handleClickOutside = (event) => {
        if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
        }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
        document.removeEventListener('mousedown', handleClickOutside);
    };
    }, []);

    const ClearButton = ({ visible, onClick }) =>
    !visible ? null : (
      <button
        type="button"
        className="filtro-clear"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        aria-label="Limpar"
      >
        ×
      </button>
    );

  if (type === 'autocomplete') {
    return (
      <div ref={ref} className={containerClass}>
        <Icon className="filtro-icon" />
        <input
          ref={inputRef}
          type="text"
          className="filtro-field"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => {
            if (inputValue.length >= 3) setOpen(true);
          }}
          disabled={disabled}
        />
        <ClearButton
          visible={!disabled && !!inputValue}
          onClick={() => {
            setInputValue('');
            setDynamicOptions([]);
            setOpen(false);
            if (typeof value === 'object') onChange?.(null);
            else onChange?.('');
          }}
        />
        {open && (
          <div className="filtro-dropdown-menu open">
            {dynamicOptions.length > 0 ? (
              dynamicOptions.map((opt) => (
                <div
                  key={opt.value}
                  className={`filtro-dropdown-item ${opt.value === value ? 'selected' : ''}`}
                  onClick={() => {
                    onChange(opt);
                    setInputValue(opt.label);
                    setOpen(false);
                  }}
                >
                  {opt.label}
                </div>
              ))
            ) : (
              <div className="filtro-dropdown-item disabled">
                Sem resultados
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (type === 'multi-autocomplete') {
    return (
      <div ref={ref} className={containerClass}>
        <Icon className="filtro-icon" />
        <div className="multi-autocomplete-selected">
          {(value || []).map((opt) => (
            <span key={opt.value} className="chip">
              {opt.label}
              <button
                type="button"
                className="chip-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange((value || []).filter((item) => item.value !== opt.value));
                }}
              >
                ×
              </button>
            </span>
          ))}
          <input
            type="text"
            className="filtro-field"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
          />
        </div>

        {open && (
          <div className="filtro-dropdown-menu open">
            {options
              .filter((opt) =>
                opt.label.toLowerCase().includes((search || '').toLowerCase())
              )
              .map((opt) => (
                <div
                  key={opt.value}
                  className="filtro-dropdown-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!(value || []).some((v) => v.value === opt.value)) {
                      onChange([...(value || []), opt]);
                    }
                    setSearch('');
                    setOpen(false);
                  }}
                >
                  {opt.label}
                </div>
              ))}

            {options.filter((opt) =>
              opt.label.toLowerCase().includes((search || '').toLowerCase())
            ).length === 0 &&
              search.trim() !== '' && (
                <div
                  className="filtro-dropdown-item add-new"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newOption = { value: search, label: search };
                    onChange([...(value || []), newOption]);
                    setSearch('');
                    setOpen(false);
                  }}
                >
                  Adicionar "{search}"
                </div>
              )}
          </div>
        )}
        <ClearButton
          visible={!disabled && Array.isArray(value) && value.length > 0}
          onClick={() => onChange?.([])}
        />
      </div>
    );
  }

    if (type === 'multi-select') {
      const allSelected = value || [];
      const visible = allSelected.slice(0, 0);
      const hiddenCount = Math.max(0, allSelected.length - visible.length);
    return (
      <div ref={ref} className={containerClass}>
        <Icon className="filtro-icon" />
        <div
          className={`multi-autocomplete-selected ${type === 'multi-select' ? 'is-multi-select' : ''}`}
          onClick={() => setOpen(true)}
        >

          <input
            type="text"
            className="filtro-field"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            disabled={disabled}
          />

          {visible.map((opt) => (
            <span key={opt.value} className="chip">
              {opt.label}
              <button
                type="button"
                className="chip-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(allSelected.filter((v) => v.value !== opt.value));
                }}
                aria-label={`Remover ${opt.label}`}
              >
                ×
              </button>
            </span>
          ))}
                   {hiddenCount > 0 && (
           <span className="chip chip-more">+{hiddenCount}</span>
         )}
        </div>

        {open && !disabled && (
          <div className="filtro-dropdown-menu open">
            {options
              .filter((opt) =>
                opt.label.toLowerCase().includes((search || '').toLowerCase())
              )
              .map((opt) => {
                const selected = (value || []).some((v) => v.value === opt.value);
                return (
                  <div
                    key={opt.value}
                    className={`filtro-dropdown-item ${selected ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (selected) {
                        onChange((value || []).filter((v) => v.value !== opt.value));
                      } else {
                        onChange([...(value || []), opt]);
                      }
                    }}
                  >
                    {opt.label}
                  </div>
                );
              })}

            {options.filter((opt) =>
              opt.label.toLowerCase().includes((search || '').toLowerCase())
            ).length === 0 && (
              <div className="filtro-dropdown-item disabled">
                Sem resultados
              </div>
            )}
          </div>
        )}
        <ClearButton
          visible={!disabled && Array.isArray(value) && value.length > 0}
         onClick={() => onChange?.([])}
        />
      </div>
    );
  }

  if (type === 'textarea') {
    const displayValue = htmlToTextareaValue(value);
    return (
      <div className="filtro-input textarea-container">
        <Icon className="filtro-icon" />
        <textarea
          className={`filtro-field filtro-textarea ${resizable ? 'resizable' : 'no-resize'}`}
          placeholder={placeholder}
          value={displayValue}
          onChange={(e) => onChange(e.target.value)}
          rows={5}
          spellCheck={true}
          lang="pt-BR"
          style={{ height: `${height}px` }}
        />
      </div>
    );
  }
  
  if (type === 'text') {
    return (
      <div className={containerClass}>
        <Icon className="filtro-icon" />
        <input
          type="text"
          className="filtro-field"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <ClearButton
          visible={!disabled && !!value}
          onClick={() => onChange?.('')}
        />
      </div>
    );
  }

  if (type === 'date') {
    return (
      <div className={containerClass}>
        <Icon className="filtro-icon" />
        <DatePicker
          selected={value}
          onChange={onChange}
          placeholderText={placeholder}
          dateFormat="dd/MM/yyyy"
          locale={ptBR}
          className="filtro-field"
          onCalendarClose={() => setOpen(false)}
          disabled={disabled}
        />
      </div>
    );
  }

  if (type === 'date-range') {
    const start = Array.isArray(value) && value[0] instanceof Date && !isNaN(value[0]) ? value[0] : null;
    const end   = Array.isArray(value) && value[1] instanceof Date && !isNaN(value[1]) ? value[1] : null;

    return (
      <div className={`filtro-input ${disabled ? 'is-disabled' : ''}`}>
        <Icon className="filtro-icon" />
        <DatePicker
          selectsRange
          startDate={start}
          endDate={end}
          onChange={(dates) => {
            const [startDate, endDate] = dates || [];
            onChange([startDate || null, endDate || null]);
          }}
          placeholderText={placeholder || 'dd/mm/aaaa - dd/mm/aaaa'}
          dateFormat="dd/MM/yyyy"
          locale={ptBR}
          className="filtro-field"
          disabled={disabled}
          onCalendarClose={() => setOpen(false)}
        />
      </div>
    );
  }


    if (type === 'time') {
    const handleChange = (e) => {
        const formatted = formatHourMask(e.target.value);
        onChange(formatted);
    };

    return (
        <div className={containerClass}>
        <Icon className="filtro-icon" />
        <input
            type="text"
            className="filtro-field"
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            maxLength={8}
        />
        </div>
    );
    }

    // type === 'select'
    return (
    <div ref={ref} className={containerClass}>
      <Icon className="filtro-icon" />
      <input
        type="text"
        className="filtro-field"
        placeholder={placeholder}
        value={
          open
            ? search
            : (typeof value === 'object'
                ? value.label
                : (options.find((o) => o.value === value)?.label || ''))
        }
        onFocus={() => {
          setOpen(true);
          setSearch('');
        }}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        onClick={(e) => {
          if (disabled) e.preventDefault();
          else e.stopPropagation();
        }}
        disabled={disabled}
      />

      {open && (
        <div className="filtro-dropdown-menu open">
           {(() => {
                const filtered = options.filter((opt) =>
                  opt.label.toLowerCase().includes((search || '').toLowerCase())
                );

                if (filtered.length === 0) {
                  return (
                    <div className="filtro-dropdown-item disabled">
                      Sem resultados
                    </div>
                  );
                }

              return filtered.map((opt) => (
              <div
                key={opt.value}
                className={`filtro-dropdown-item ${
                  (typeof value === 'object' ? value.value : value) === opt.value
                    ? 'selected'
                    : ''
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeof value === 'object') {
                    onChange(opt);
                  } else {
                    onChange(opt.value);
                  }
                  setSearch('');
                  setOpen(false);
                }}
              >
                {opt.label}
              </div>
            ));
          })()}
        </div>
      )}
      <ClearButton
        visible={
          !disabled &&
          (
            (typeof value === 'object' && !!value) ||
            (!!value && typeof value !== 'object')
          )
        }
        onClick={() => {
          setSearch('');
          if (typeof value === 'object') onChange?.(null);
          else onChange?.('');
        }}
      />
    </div>
  );
}

export default Filter;